"use client"

import { Form, Formik } from "formik"
import { Loader2, Save, ShieldCheck } from "lucide-react"

import { FormInput } from "@/components/form-inputs"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { useSaveDeliveryPiiRetentionPolicyMutation } from "@/hooks/mutations/delivery-retention"
import { useDeliveryPiiRetentionPolicyQuery } from "@/hooks/queries/delivery-retention"
import {
  DELIVERY_PII_RETENTION_DEFAULT_DAYS,
  DELIVERY_PII_RETENTION_MAX_DAYS,
  DELIVERY_PII_RETENTION_MIN_DAYS,
} from "@/lib/deliveries/pii-retention-config"
import { useTranslations } from "next-intl"


type DeliveryPiiRetentionFormValues = {
  recipientPiiRetentionDays: string
}

export function DeliveryPiiRetentionForm() {
  const tUi = useTranslations("Deliveries.DeliveriesDeliveryPiiRetentionForm")

  const policyQuery = useDeliveryPiiRetentionPolicyQuery()
  const saveMutation = useSaveDeliveryPiiRetentionPolicyMutation()

  if (policyQuery.isLoading) {
    return <div className="h-40 animate-pulse rounded-md bg-muted" />
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4" />
          <span><span key={tUi("recipientDataRetention")}>{tUi("recipientDataRetention")}</span></span>
        </CardTitle>
        <CardDescription>
          <span key={tUi("recipientContactAndAddressDataIsAutomaticallyRedacted")}>{tUi("recipientContactAndAddressDataIsAutomaticallyRedacted")}</span></CardDescription>
      </CardHeader>
      <CardContent>
        {policyQuery.error ? (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>
              <span key={tUi("theDeliveryPrivacyPolicyCouldNotBeLoaded")}>{tUi("theDeliveryPrivacyPolicyCouldNotBeLoaded")}</span></AlertDescription>
          </Alert>
        ) : null}
        <Formik<DeliveryPiiRetentionFormValues>
          enableReinitialize
          initialValues={{
            recipientPiiRetentionDays: String(
              policyQuery.data?.recipientPiiRetentionDays
                ?? DELIVERY_PII_RETENTION_DEFAULT_DAYS,
            ),
          }}
          validate={validateRetentionPolicy}
          onSubmit={(values, helpers) => {
            saveMutation.mutate({
              recipientPiiRetentionDays: Number(
                values.recipientPiiRetentionDays,
              ),
              setErrors: helpers.setErrors,
            }, {
              onSettled: () => helpers.setSubmitting(false),
            })
          }}
        >
          {({ isSubmitting }) => (
            <Form className="max-w-sm space-y-4">
              <FormInput
                name="recipientPiiRetentionDays"
                label={tUi("retentionPeriodDays")}
                type="number"
                min={DELIVERY_PII_RETENTION_MIN_DAYS}
                max={DELIVERY_PII_RETENTION_MAX_DAYS}
                step={1}
                inputMode="numeric"
                description={tUi("choose190DaysLongerRetentionRequiresAn")}
              />
              <Button
                type="submit"
                disabled={saveMutation.isPending || isSubmitting}
              >
                {saveMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                <span><span key={tUi("savePrivacyPolicy")}>{tUi("savePrivacyPolicy")}</span></span>
              </Button>
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  )
}

function validateRetentionPolicy(
  values: DeliveryPiiRetentionFormValues,
): Partial<Record<keyof DeliveryPiiRetentionFormValues, string>> {
  const days = Number(values.recipientPiiRetentionDays)
  if (
    !Number.isInteger(days)
    || days < DELIVERY_PII_RETENTION_MIN_DAYS
    || days > DELIVERY_PII_RETENTION_MAX_DAYS
  ) {
    return {
      recipientPiiRetentionDays:
        `Use a whole number from ${DELIVERY_PII_RETENTION_MIN_DAYS} to ${DELIVERY_PII_RETENTION_MAX_DAYS}.`,
    }
  }
  return {}
}
